import { createApp } from 'vue'
import App from './App.vue'
import { createHead } from '@vueuse/head'
import router from './router'
import { createPinia } from 'pinia';
import VueLazyload from 'vue-lazyload';
import { pideOnAir } from './services/onair';
import { recordarParametrosDev } from './utils/parametrosDev';
import { i18n, ponIdioma } from './i18n';
import { IDIOMA, IDIOMA_BASE, IDIOMAS_PUBLICOS, sinIdioma, destinoDeLaVisita, LLAVE_IDIOMA_DESDE } from './i18n/idioma';
import { medir } from './services/medicion';

import 'bootstrap/dist/css/bootstrap.min.css';
import './assets/scss/tokens.scss';
import './assets/scss/type-classes.scss';
import './assets/scss/astra-styles.scss';
import './assets/scss/ds-tokens.scss';
import '@fortawesome/fontawesome-free/css/all.css';

// 🔇 Deshabilitar console.log/warn/debug (mantener solo console.error para errores críticos)
console.log = () => {};
console.debug = () => {};
console.warn = () => {};
// console.error se mantiene activo para errores críticos

// 🔴 **La primera visita va al idioma del navegador** (Pablo, 04.11.26), antes
// de montar nada. Ver `destinoDeLaVisita` en `i18n/idioma.js`: solo sin prefijo,
// solo una vez y nunca a un buscador. Con `IDIOMAS_PUBLICOS` apagado no hace
// nada.
const destino = destinoDeLaVisita();
if (destino) {
  try {
    // La página de destino lo cuenta a Umami: aquí aún no ha cargado.
    sessionStorage.setItem(LLAVE_IDIOMA_DESDE, 'deteccion');
  } catch (e) {
    // Sin almacenamiento, sin evento.
  }
  location.replace(destino);
} else {
  arrancar();
}

function arrancar() {
  // 🔴 **`onair.json` sale antes de montar la aplicación** en la portada y en Radio,
  // que son las que lo pintan (08.10.26, la primera carga lenta). Queda en la
  // memoria de `services/onair.js` y la vista lo encuentra hecho o en camino. No
  // es un `<link rel="preload">` en `index.html` porque ese saldría en todas las
  // páginas y Chrome avisa en consola de lo precargado y no usado.
  if (sinIdioma(location.pathname) === '/' || sinIdioma(location.pathname).startsWith('/radio')) {
    pideOnAir();
  }

  // En local, los parámetros de prueba (`?vista-previa`, `?encuadre`,
  // `?probar-foto`) se guardan antes de que el router suelte la query.
  recordarParametrosDev();

  const app = createApp(App);
  const head = createHead();
  app.use(VueLazyload);
  app.use(head);
  app.use(createPinia());
  app.use(router);
  app.use(i18n);

  // 🔴 **Hasta publicar los idiomas, `/en`, `/ca` y `/fr` no se indexan.** Se
  // enciende con `IDIOMAS_PUBLICOS`, junto al selector y los `hreflang`
  // (`PROPUESTA_TRADUCCIONES_WEB.md` § 5).
  if (IDIOMA !== IDIOMA_BASE && !IDIOMAS_PUBLICOS) {
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex';
    document.head.appendChild(robots);
  }

  // El idioma se carga antes de montar, para que la primera pintura ya salga en
  // el suyo. En español no espera nada: va dentro del bundle.
  //
  // ⚠️ El título y la descripción de `index.html` están en español. En los otros
  // idiomas se cambian aquí, en las mismas etiquetas, antes de montar; cada vista
  // pone después el suyo con `useHead`.
  ponIdioma(IDIOMA)
    .then(() => {
      if (IDIOMA === IDIOMA_BASE) return;
      const { t } = i18n.global;
      const pon = (selector, texto) => document.querySelector(selector)?.setAttribute('content', texto);
      document.title = t('marco.meta.titulo');
      pon('meta[name="description"]', t('marco.meta.descripcion'));
      pon('meta[property="og:title"]', t('marco.meta.compartirTitulo'));
      pon('meta[property="og:description"]', t('marco.meta.compartirDescripcion'));
      pon('meta[name="twitter:title"]', t('marco.meta.compartirTitulo'));
      pon('meta[name="twitter:description"]', t('marco.meta.compartirDescripcion'));
    })
    .finally(() => app.mount('#app'));

  // Analítica (acordado con el servidor): de dónde salió el idioma de la visita,
  // la detección de la primera visita o el selector. Lo apunta quien recarga
  // (`destinoDeLaVisita` arriba, `cambiaIdioma` en `i18n/idioma.js`) y se cuenta
  // aquí, en la página nueva, porque la vieja se va antes de que Umami envíe.
  try {
    const desde = sessionStorage.getItem(LLAVE_IDIOMA_DESDE);
    if (desde) {
      sessionStorage.removeItem(LLAVE_IDIOMA_DESDE);
      medir('idioma_elegido', { idioma: IDIOMA, desde });
    }
  } catch (e) {
    // Sin almacenamiento, sin evento.
  }
}

// 📦 Registrar Service Worker para PWA y cache offline
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        console.log('✅ Service Worker registrado:', registration.scope);

        // Forzar activación inmediata del nuevo SW sin esperar cierre de pestañas
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed') {
              // Enviar SKIP_WAITING para activar el nuevo SW inmediatamente
              newWorker.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });
      })
      .catch((error) => {
        console.error('❌ Error registrando Service Worker:', error);
      });
  });
}

// 🔴 **Que nadie se quede con el service worker de ayer.**
//
// El navegador solo busca una versión nueva al CARGAR un documento, y esta web
// es de una sola página: moverse por ella no carga nada, así que una pestaña
// abierta puede seguir semanas con el de entonces. Lo preguntó el servidor el
// 21.09.26 (`ALTAS_SIGUEN_FALLANDO_WEB.md` §2) y tenía razón en preguntarlo: el
// arreglo del alta salió a las 11:48 y esa noche volvió a fallar un alta, con el
// viejo como primer sospechoso.
//
// Así que se pregunta al volver a la pestaña, con freno de diez minutos para no
// pedir el fichero a cada cambio de ventana. El relevo en sí ya estaba resuelto:
// el service worker hace `skipWaiting` al instalarse y `clients.claim` al
// activarse, así que en cuanto se descubre uno nuevo toma el mando sin esperar a
// que se cierren las pestañas.
if ('serviceWorker' in navigator) {
  const FRENO = 10 * 60 * 1000;
  let ultima = Date.now();

  document.addEventListener('visibilitychange', () => {
    if (document.hidden || Date.now() - ultima < FRENO) return;
    ultima = Date.now();
    navigator.serviceWorker.getRegistration().then((r) => r && r.update()).catch(() => {});
  });
}
